import type {
  InspirationMediaType,
  InspirationStatus,
  InspirationUsage,
  StaffInspirationRow,
} from '../../types/portalInspiration.js';
import { publishProblems } from './entryRules.js';

// PORTAL INSPIRATION — staff list view logic. PURE. The collection is small (hundreds), so the
// server returns everything and the page filters in memory.

export type StaffFilters = {
  mediaType: InspirationMediaType;
  status: InspirationStatus | '';
  usage: InspirationUsage | '';
  tag: string;
  search: string;
};

export const DEFAULT_FILTERS: StaffFilters = { mediaType: 'video', status: '', usage: '', tag: '', search: '' };

/** What a row is called in the list: the title, else the original file name (a fresh upload has no title yet). */
export function rowLabel(r: StaffInspirationRow): string {
  const t = r.entry.title.trim();
  return t !== '' ? t : (r.asset?.originalFilename ?? 'Untitled upload');
}

export function filterStaffRows(rows: readonly StaffInspirationRow[], f: StaffFilters): StaffInspirationRow[] {
  const q = f.search.trim().toLowerCase();
  return rows.filter((r) => {
    if (r.entry.mediaType !== f.mediaType) return false;
    if (f.status !== '' && r.entry.status !== f.status) return false;
    if (f.usage !== '' && r.entry.usage !== f.usage) return false;
    if (f.tag !== '' && !r.entry.tags.includes(f.tag)) return false;
    if (q !== '') {
      const hay = [rowLabel(r), r.entry.tags.join(' '), r.asset?.originalFilename ?? ''].join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

export function tabCounts(rows: readonly StaffInspirationRow[]): Record<InspirationMediaType, number> {
  return {
    video: rows.filter((r) => r.entry.mediaType === 'video').length,
    image: rows.filter((r) => r.entry.mediaType === 'image').length,
  };
}

export function allTags(rows: readonly StaffInspirationRow[]): string[] {
  return [...new Set(rows.flatMap((r) => r.entry.tags))].sort();
}

/** Why a row cannot be published right now, in words, or null. Mirrors the server (entry rules + file state). */
export function publishBlocker(r: StaffInspirationRow): string | null {
  if (r.entry.status === 'published') return null;
  if (!r.asset || r.asset.deleted) return 'The file is missing.';
  if (r.asset.lifecycle !== 'ready') {
    return r.asset.lifecycle === 'failed'
      ? `The file failed to process${r.asset.failureReason ? `: ${r.asset.failureReason}` : '.'}`
      : 'The file is still processing.';
  }
  const problems = publishProblems(r.entry);
  if (problems.length > 0) return problems.join(' ');
  if (r.reach.accountCount === 0) return 'The audience matches no accounts.';
  return null;
}

/** The status shown on a card: the entry's own, with the file's state when it is not yet ready. */
export function statusLabel(r: StaffInspirationRow): { text: string; tone: 'ok' | 'warn' | 'bad' | 'muted' } {
  if (r.asset?.deleted) return { text: 'File deleted', tone: 'bad' };
  if (r.asset?.lifecycle === 'failed') return { text: 'File failed', tone: 'bad' };
  if (r.asset && r.asset.lifecycle !== 'ready') return { text: 'Processing', tone: 'warn' };
  return r.entry.status === 'published' ? { text: 'Published', tone: 'ok' } : { text: 'Draft', tone: 'muted' };
}

export function bulkSummary(results: ReadonlyArray<{ ok: boolean; error?: string }>): string {
  const ok = results.filter((r) => r.ok).length;
  const bad = results.length - ok;
  return bad === 0 ? `${ok} done.` : `${ok} done, ${bad} refused.`;
}

/** The sentence under the audience picker: the library's own wording, from the same numbers. */
export function inspirationReachSentence(r: { accountCount: number; brandCount: number }): string {
  const a = r.accountCount;
  const b = r.brandCount;
  const accounts = `${a} account${a === 1 ? '' : 's'}`;
  if (b === 0) return `This will appear for ${accounts}.`;
  return `This will appear for ${accounts} across ${b} brand${b === 1 ? '' : 's'}.`;
}

export function inspirationAccessSentence(r: { accountCount: number; accountsWithPortalAccess: number }): string {
  const n = r.accountsWithPortalAccess;
  if (n === 0) return 'None of them have portal access yet.';
  return n === r.accountCount ? 'All of them have portal access today.' : `${n} of them have portal access today.`;
}
